import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUIVersion } from "#hooks";
import { cn, customTv } from "#utils";
import {
  Dialog,
  DialogTrigger,
  type DialogTriggerProps,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Popover,
  type Selection,
} from "react-aria-components";

import { Button, type ButtonProps } from "../Button";
import { CircularProgress } from "../CircularProgress";
import { SearchField } from "../SearchField";
import { Skeleton } from "../Skeleton";

export type PopoverPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top left"
  | "top right"
  | "bottom left"
  | "bottom right"
  | "left top"
  | "left bottom"
  | "right top"
  | "right bottom";

export interface MultiDropdownOption {
  id: string;
  /** Display content — can be a ReactNode for rich rendering. Provide searchLabel for search/accessibility when name is ReactNode. */
  name: ReactNode;
  /** Text used for search filtering and accessibility. Required when name is ReactNode. */
  searchLabel?: string;
  /** Optional descriptive text displayed below the option name. */
  assistiveText?: string;
  /** Group name — options sharing the same group are rendered under a common section header. */
  group?: string;
}

const dialogStyles = customTv({
  base: "flex min-w-[12.5rem] flex-col gap-y-1 rounded-1 border border-outline-decorative bg-background-secondary px-1 py-2 shadow-md outline-none",
  variants: {
    uiVersion: {
      v1: "bg-neutral-white border-neutral-300 rounded-xs shadow-lg",
      v2: "",
    },
  },
});

const groupHeaderStyles = customTv({
  base: "pointer-events-none select-none",
  variants: {
    uiVersion: {
      v1: "px-2 py-1 font-bold text-body-small text-text-primary",
      v2: "p-3xs text-title-medium border-b border-outline-decorative mx-xs",
    },
  },
});

export function MultiDropdownItemLabel({ item }: { item: MultiDropdownOption }) {
  if (!item.assistiveText) {
    return item.name;
  }

  return (
    <span>
      <span className="block">{item.name}</span>
      <span className="block text-label-small text-text-secondary">{item.assistiveText}</span>
    </span>
  );
}

export interface MultiDropdownCTAConfig extends Omit<ButtonProps, "slot"> {
  /**
   * When true, clicking the CTA button will close the dropdown.
   * Uses react-aria `slot="close"` under the hood.
   */
  closeOnClick?: boolean;
}

export interface MultiDropdownBaseProps extends Omit<DialogTriggerProps, "children"> {
  /** Trigger element that opens the dropdown */
  trigger: ReactNode;
  /** Popover placement relative to the trigger */
  placement?: PopoverPlacement;
  /** Currently selected options (controlled mode) */
  value?: MultiDropdownOption[];
  /** Initial selected options for uncontrolled mode. Ignored when `value` is provided. */
  defaultValue?: MultiDropdownOption[];
  /** Callback fired when the selection changes */
  onChange?: (selected: MultiDropdownOption[]) => void;
  /** Additional class name for the dialog container */
  className?: string;
  /** Accessible label for the dropdown */
  label: string;
  /** List of options to render */
  options: MultiDropdownOption[];
  /** CTA bar rendered above the options list */
  topCTA?: {
    left?: MultiDropdownCTAConfig;
    right?: MultiDropdownCTAConfig;
  };
  /** CTA bar rendered below the options list */
  bottomCTA?: {
    left?: MultiDropdownCTAConfig;
    right?: MultiDropdownCTAConfig;
  };
  /** Search callback. When provided, a search field is shown above the list. */
  onSearch?: (searchValue: string) => void;
  /** Placeholder text for the search field */
  searchPlaceholder?: string;
  /** Controlled search value */
  searchValue?: string;
  /** When true, the popover will match the width of the trigger element */
  matchTriggerWidth?: boolean;
  /** Called when the user scrolls near the bottom of the list. Enables infinite scroll. */
  loadMore?: () => void;
  /** Whether there are more items available to load */
  hasMore?: boolean;
  /** Whether more items are currently being fetched */
  isLoadingMore?: boolean;
  /** When true, shows loading skeletons in place of the option list */
  isPending?: boolean;
  /**
   * Render function for each option item.
   * Receives the option and whether it's selected. Selection is managed by the ListBox.
   */
  renderItem: (
    item: MultiDropdownOption,
    isSelected: boolean,
    isHovered: boolean,
    isFocused: boolean,
  ) => ReactNode;
}

export type MultiDropdownProps = Omit<MultiDropdownBaseProps, "renderItem">;

function CTAButton({ closeOnClick, variant, ...buttonProps }: MultiDropdownCTAConfig) {
  return (
    <Button
      {...buttonProps}
      slot={closeOnClick ? "close" : undefined}
      variant={variant ?? "tertiary"}
    />
  );
}

function CTABar({
  left,
  right,
  className,
}: {
  left?: MultiDropdownCTAConfig;
  right?: MultiDropdownCTAConfig;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-between", className)}>
      {left ? <CTAButton {...left} /> : null}
      {right ? <CTAButton {...right} /> : null}
    </div>
  );
}

export function MultiDropdownBase({
  trigger,
  placement = "bottom",
  label,
  value: controlledValue,
  defaultValue,
  className,
  onChange,
  options,
  topCTA,
  bottomCTA,
  onSearch,
  searchPlaceholder = "Search",
  searchValue,
  matchTriggerWidth,
  loadMore,
  hasMore,
  isLoadingMore,
  isPending,
  renderItem,
  ...dialogTriggerProps
}: MultiDropdownBaseProps) {
  const uiVersion = useUIVersion();

  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<MultiDropdownOption[]>(defaultValue ?? []);
  const selectedOptions = isControlled ? controlledValue : internalValue;

  const showSearch = !!onSearch;

  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = listContainerRef.current;
    if (!el || isLoadingMore || !hasMore || !loadMore) return;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 8) {
      loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  useEffect(() => {
    if (!matchTriggerWidth) return;

    const trigger = triggerRef.current;
    if (!trigger) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTriggerWidth(entry.target.clientWidth);
      }
    });

    observer.observe(trigger);
    return () => {
      observer.unobserve(trigger);
    };
  }, [matchTriggerWidth]);

  const selectedKeys = useMemo(() => new Set(selectedOptions.map((o) => o.id)), [selectedOptions]);

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys === "all") {
        // Preserve selections hidden by search filter (options may be filteredOptions from parent).
        const visibleIds = new Set(options.map((o) => o.id));
        const retained = selectedOptions.filter((o) => !visibleIds.has(o.id));
        const newOptions = [...retained, ...options];
        if (!isControlled) setInternalValue(newOptions);
        onChange?.(newOptions);
        return;
      }

      const retained = selectedOptions.filter((o) => keys.has(o.id));
      const retainedIds = new Set(retained.map((o) => o.id));
      const added = options.filter((o) => keys.has(o.id) && !retainedIds.has(o.id));
      const newOptions = [...retained, ...added];

      if (!isControlled) setInternalValue(newOptions);
      onChange?.(newOptions);
    },
    [options, selectedOptions, isControlled, onChange],
  );

  const getTextValue = (item: MultiDropdownOption) =>
    item.searchLabel ?? (typeof item.name === "string" ? item.name : item.id);

  const renderListBoxItems = (items: MultiDropdownOption[]) =>
    items.map((item) => (
      <ListBoxItem
        className="flex cursor-default items-center gap-xs p-2 focus:outline-none"
        id={item.id}
        key={item.id}
        textValue={getTextValue(item)}
      >
        {({ isSelected, isHovered, isFocused }) =>
          renderItem(item, isSelected, isHovered, isFocused)
        }
      </ListBoxItem>
    ));

  const renderOptionList = () => {
    // Collect items into groups while preserving first-appearance order of group names.
    // This merges non-contiguous items that share the same group label.
    const groupOrder: string[] = [];
    const groupedItems = new Map<string, MultiDropdownOption[]>();

    options.forEach((item) => {
      const key = item.group ?? "";
      if (!groupedItems.has(key)) {
        groupOrder.push(key);
        groupedItems.set(key, []);
      }

      const group = groupedItems.get(key);
      if (group) {
        group.push(item);
      }
    });

    return groupOrder.flatMap((group) => {
      const items = groupedItems.get(group);

      if (!items) {
        return [];
      }

      if (!group) {
        return renderListBoxItems(items);
      }

      return (
        <ListBoxSection key={`group-${group}`}>
          <Header className={groupHeaderStyles({ uiVersion })}>{group}</Header>
          {renderListBoxItems(items)}
        </ListBoxSection>
      );
    });
  };

  return (
    <DialogTrigger {...dialogTriggerProps}>
      <span className="inline-flex" ref={matchTriggerWidth ? triggerRef : undefined}>
        {trigger}
      </span>
      <Popover
        className="scrollbar-custom overscroll-contain"
        placement={placement}
        style={matchTriggerWidth ? { minWidth: triggerWidth } : undefined}
      >
        <Dialog
          aria-label={`Dialog Multi Dropdown ${label}`}
          className={dialogStyles({ uiVersion, className })}
        >
          {showSearch ? (
            <div className="flex p-0-5">
              <SearchField
                className="w-full"
                inputProps={{
                  placeholder: searchPlaceholder,
                }}
                onChange={onSearch}
                value={searchValue}
              />
            </div>
          ) : null}

          {topCTA ? <CTABar left={topCTA.left} right={topCTA.right} /> : null}

          <div
            className="flex max-h-[19.75rem] flex-col overflow-y-auto overscroll-y-contain"
            onScroll={handleScroll}
            ref={listContainerRef}
          >
            {isPending ? (
              <div className="flex flex-col gap-y-1 p-2">
                <Skeleton width="100%" />
                <Skeleton width="100%" />
                <Skeleton width="100%" />
                <Skeleton width="100%" />
              </div>
            ) : options.length === 0 ? (
              <div className="p-2 text-label-medium text-text-secondary">No results found...</div>
            ) : (
              <ListBox
                aria-label={`list-${label}`}
                className="flex flex-col gap-y-1"
                onSelectionChange={handleSelectionChange}
                selectedKeys={selectedKeys}
                selectionMode="multiple"
                // Without this, selection fires on mousedown for mouse pointers — the
                // resulting layout shift (new tag → trigger grows → popover repositions)
                // makes the trailing mouseup/click land on a different element, which
                // can toggle the just-selected item back off.
                shouldSelectOnPressUp
              >
                {renderOptionList()}
              </ListBox>
            )}
            {isLoadingMore ? (
              <div className="flex justify-center py-xs">
                <CircularProgress size="small" />
              </div>
            ) : null}
          </div>

          {bottomCTA ? (
            <CTABar
              className="border-t border-outline-decorative pt-3"
              left={bottomCTA.left}
              right={bottomCTA.right}
            />
          ) : null}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
